import { useEffect } from 'react';
import { useAccount, useConfig } from 'wagmi';
import { SchemaSignerDto } from '../../../generated/apiTypes';
import { useActiveSigner } from '../../../hooks/useActiveSigner';
import { useDataForSigning } from '../../../hooks/useDataForSigning';
import { useModalState } from '../../../hooks/useModalState';
import { useActiveSelectedSubaccount } from '../../../hooks/useSelectedSubaccount';
import { isUserRejectedError } from '../../../lib/errors';
import { formatExpirationDuration } from '../../../lib/formatters';
import { showToast } from '../../../lib/showToast';
import { signerRenewedToast } from '../../../lib/toasts';
import { assertRequiredData } from '../../../lib/typeGuards';
import { Alert } from '../../Alert';
import { Button } from '../../catalyst-ui-kit/button';
import { Loader } from '../../Loader';
import { useSettingsState } from '../SettingsState';
import { ALERT_CONTENT } from '../signerAlertContent';
import { useNewSigner } from '../signerHooks';

const SettingsRenewPanelUi: React.FC<{
  signerLoading: boolean;
  signer: SchemaSignerDto | undefined;
  hasRequiredDataErrors: boolean;
  requiredDataPending: boolean;
  renewMutation: ReturnType<typeof useNewSigner>;
  onRenewClick: () => void;
}> = ({
  signerLoading,
  signer,
  hasRequiredDataErrors,
  requiredDataPending,
  renewMutation,
  onRenewClick,
}) => {
  const alert = (() => {
    switch (true) {
      case hasRequiredDataErrors && !requiredDataPending: {
        return ALERT_CONTENT.REQUIRED_DATA_ERROR;
      }
      case renewMutation.error && !isUserRejectedError(renewMutation.error): {
        return {
          variant: ALERT_CONTENT.RENEW_ERROR.variant,
          message: ALERT_CONTENT.RENEW_ERROR.getMessage(renewMutation.error),
        };
      }
      case renewMutation.isPending: {
        return ALERT_CONTENT.RENEW_PENDING;
      }
      default: {
        return null;
      }
    }
  })();

  return (
    <div className="flex flex-col gap-3 text-xs font-medium text-contentSecondary sm:p-4">
      <p>Renew your signer to continue using One-Click Trading.</p>
      <div className="flex items-center justify-between">
        <span>Expires in</span>
        {signerLoading ? (
          <span className="h-5 w-6 animate-pulse rounded bg-gray-200"></span>
        ) : (
          <span className="text-contentPrimary">{formatExpirationDuration(signer?.expiresAt)}</span>
        )}
      </div>
      {alert && <Alert variant={alert.variant}>{alert.message}</Alert>}
      <Button
        disabled={Boolean(
          renewMutation.error ||
            renewMutation.isPending ||
            requiredDataPending ||
            hasRequiredDataErrors
        )}
        onClick={() => onRenewClick()}
        color="blue"
        className="flex items-center justify-center gap-1"
      >
        {renewMutation.isPending ? <Loader /> : null} Renew Signer
      </Button>
    </div>
  );
};

export const SettingsRenewPanel: React.FC = () => {
  const settingsModal = useModalState(({ settingsModal }) => settingsModal);
  const setSettingsModal = useModalState(({ setSettingsModal }) => setSettingsModal);
  const resetPanel = useSettingsState(({ resetPanels }) => resetPanels);

  const { address } = useAccount();
  const selectedSubaccount = useActiveSelectedSubaccount();
  const config = useConfig();
  const {
    domain,
    signedTypedDataTypes,
    error: requiredDataError,
    isPending: requiredDataPending,
  } = useDataForSigning();
  const { data: signer, isLoading: signerLoading, error: signerError } = useActiveSigner();
  const hasRequiredDataErrors = Boolean(requiredDataError || signerError);

  const renewMutation = useNewSigner('renew');

  const { isPending: renewPending, reset: renewReset } = renewMutation;

  useEffect(() => {
    if (!renewPending && settingsModal !== 'renew') {
      renewReset();
    }
  }, [settingsModal, renewReset, renewPending]);

  const mutationParams = {
    subaccountName: selectedSubaccount?.name,
    subaccountId: selectedSubaccount?.id,
    address,
    domain,
    signedTypedDataTypes,
    config,
  };

  const onRenewClick = () => {
    assertRequiredData(mutationParams);

    renewMutation.mutate(mutationParams, {
      onSuccess: () => {
        resetPanel();
        setSettingsModal('closed');
        showToast(signerRenewedToast());
      },
    });
  };

  return (
    <SettingsRenewPanelUi
      signerLoading={signerLoading}
      signer={signer}
      hasRequiredDataErrors={hasRequiredDataErrors}
      requiredDataPending={requiredDataPending}
      renewMutation={renewMutation}
      onRenewClick={onRenewClick}
    />
  );
};
